import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Typography
} from '@mui/material';
import { sendContactEmail } from '../utils/emailService';

/**
 * ContactDialog displays a modern, card-style contact form dialog with two-column layout and a prominent submit button. All fields are preserved. Shows a popup when email is sent and closes dialog with the popup.
 */
const sectors = [
  'Banking & Insurance',
  'Manufacturing',
  'Healthcare',
  'Retail',
  'Telecommunications',
  'Public Sector',
  'Energy & Utilities',
  'Transportation',
  'Other'
];

const ContactDialog = ({ open, onClose, formData: calculationData }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.company || !form.phone) {
      setError('Please fill in all required fields');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const emailData = {
        ...form,
        calculationData: calculationData
      };
      const result = await sendContactEmail(emailData);
      if (result.success) {
        setSuccess(true);
        setSnackbar({
          open: true,
          message: 'Message sent successfully! Our expert will contact you soon.',
          severity: 'success'
        });
      } else {
        setError(result.error || 'Failed to send the message. Please try again.');
        setSnackbar({
          open: true,
          message: result.error || 'Failed to send the message. Please try again.',
          severity: 'error'
        });
      }
    } catch (err) {
      setError('Failed to send the message. Please try again.');
      setSnackbar({
        open: true,
        message: err.message || 'Failed to send the message. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
    if (success) {
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 200);
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 5,
            boxShadow: '0 8px 32px rgba(219,29,43,0.10)',
            background: 'white',
            p: 0
          }
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 4 }, pb: 3 }}>
          <DialogTitle sx={{
            p: 0,
            mb: 1.5,
            fontWeight: 'bold',
            fontSize: { xs: '1.7rem', sm: '2.1rem' },
            color: '#231f20',
            bgcolor: 'transparent',
            textAlign: 'left',
            letterSpacing: '-0.5px'
          }}>
            Get in Touch
          </DialogTitle>
          <Typography sx={{ color: '#666', mb: 3, fontSize: '1.1rem' }}>
            Leave your message and we'll get back to you shortly.
          </Typography>
          <DialogContent sx={{ p: 0 }}>
            <Box
              component="form"
              onSubmit={e => { e.preventDefault(); handleSubmit(); }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                mt: 1
              }}
            >
              <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                <TextField
                  name="name"
                  label="Your name"
                  value={form.name}
                  onChange={handleChange}
                  fullWidth
                  required
                  variant="outlined"
                  disabled={loading}
                  sx={{
                    bgcolor: '#f5f5f5',
                    borderRadius: 3,
                    input: { borderRadius: 3 },
                  }}
                  InputProps={{
                    sx: { borderRadius: 3, fontSize: '1rem' }
                  }}
                />
                <TextField
                  name="email"
                  label="Email address"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  fullWidth
                  required
                  variant="outlined"
                  disabled={loading}
                  sx={{
                    bgcolor: '#f5f5f5',
                    borderRadius: 3,
                    input: { borderRadius: 3 },
                  }}
                  InputProps={{
                    sx: { borderRadius: 3, fontSize: '1rem' }
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                <TextField
                  name="company"
                  label="Company"
                  value={form.company}
                  onChange={handleChange}
                  fullWidth
                  required
                  variant="outlined"
                  disabled={loading}
                  sx={{
                    bgcolor: '#f5f5f5',
                    borderRadius: 3,
                    input: { borderRadius: 3 },
                  }}
                  InputProps={{
                    sx: { borderRadius: 3, fontSize: '1rem' }
                  }}
                />
                <TextField
                  name="phone"
                  label="Phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  fullWidth
                  required
                  variant="outlined"
                  disabled={loading}
                  sx={{
                    bgcolor: '#f5f5f5',
                    borderRadius: 3,
                    input: { borderRadius: 3 },
                  }}
                  InputProps={{
                    sx: { borderRadius: 3, fontSize: '1rem' }
                  }}
                />
              </Box>
              <TextField
                name="message"
                label="Message"
                value={form.message}
                onChange={handleChange}
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                disabled={loading}
                sx={{
                  bgcolor: '#f5f5f5',
                  borderRadius: 3,
                  textarea: { borderRadius: 3 },
                }}
                InputProps={{
                  sx: { borderRadius: 3, fontSize: '1rem' }
                }}
              />
              {error && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  Message sent successfully! Our expert will contact you soon.
                </Alert>
              )}
              <Button
                type="submit"
                variant="contained"
                sx={{
                  bgcolor: '#ff3576',
                  color: 'white',
                  borderRadius: 10,
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  px: 5,
                  py: 1.5,
                  mt: 1,
                  boxShadow: '0 2px 8px rgba(255,53,118,0.10)',
                  '&:hover': {
                    bgcolor: '#db1d2b',
                  },
                  alignSelf: 'flex-start',
                  minWidth: '120px'
                }}
                disabled={loading || !form.name || !form.email || !form.company || !form.phone}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Submit'
                )}
              </Button>
            </Box>
          </DialogContent>
        </Box>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ContactDialog; 